import type { ContagemPorDia } from '../types/auditoria';

/** "YYYY-MM-DD" de um instante, em dia-calendário UTC — usado só pra
 *  ENUMERAR a janela de dias a preencher (aritmética em UTC evita o
 *  off-by-one de fuso que `Date.setDate` sofre perto da meia-noite local;
 *  a "data" aqui é uma abstração de dia-calendário, não um instante). Não é
 *  suposição sobre o calendário que o backend usou pra montar `serie` — ver
 *  `preencherDiasZerados` abaixo pra por que isso não pode dar match perfeito
 *  com a entrada. */
function isoDoDiaUtc(ms: number): string {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dia = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${dia}`;
}

/**
 * Preenche os dias sem evento com `contagem: 0`. `porDia`/`ativosPorDia` vêm
 * de `GROUP BY` no backend — dia sem linha simplesmente não aparece na
 * resposta. Sem isso, o BarChart categórico desenha dias esparsos como barras
 * adjacentes, sugerindo continuidade (uso contínuo) que não existe.
 *
 * Devolve a UNIÃO da janela local (últimos `dias` dias corridos terminando em
 * `referencia`, calendário do browser) com qualquer dia PRESENTE na entrada —
 * mesmo os que caem fora dessa janela —, ordenada cronologicamente. A união
 * existe porque os dois chamadores agrupam em calendários DIFERENTES do lado
 * do backend: `/auditoria/numeros` agrupa `porDia` por `CriadoEm.Date` sobre
 * uma coluna `timestamptz` (= dia UTC), enquanto `/telemetria/uso` já grava
 * `telemetria_uso_diario.Dia` em dia LOCAL (`TelemetriaCollector.cs:56`). Em
 * fusos negativos (ex. UTC-3), todo evento depois de ~21h local cai no
 * bucket UTC de amanhã — uma chave que a janela LOCAL calculada aqui não
 * geraria. Um preenchimento que só emitisse a janela descartaria essa chave
 * em silêncio: o gráfico "Eventos por dia" sub-reportaria a última barra
 * enquanto o KPI "Eventos hoje" da mesma tela (que soma `total`, sem esse
 * corte) continua contando o evento — gráfico e card discordando toda noite.
 * Preencher buraco nunca pode significar perder dado.
 *
 * `referencia` é parâmetro (não `new Date()` direto no corpo) pra a função
 * ficar pura e testável sem mockar relógio.
 */
export function preencherDiasZerados(
  serie: ContagemPorDia[],
  dias: number,
  referencia: Date = new Date(),
): ContagemPorDia[] {
  const porDia = new Map(serie.map((s) => [s.dia, s.contagem]));
  const hojeUtc = Date.UTC(referencia.getFullYear(), referencia.getMonth(), referencia.getDate());

  const chaves = new Set<string>();
  for (let i = dias - 1; i >= 0; i--) {
    chaves.add(isoDoDiaUtc(hojeUtc - i * 86_400_000));
  }
  for (const chave of porDia.keys()) chaves.add(chave);

  return [...chaves].sort().map((dia) => ({ dia, contagem: porDia.get(dia) ?? 0 }));
}
